/**
 * /solutions/explore/:chatId — an Explore conversation; also
 * /solutions/validation/first-line-support/:chatId (`surface="support"`),
 * where a declined question can be escalated as a ticket (`surface.ts`).
 *
 * History is loaded from the server (durable), so reopening a chat restores
 * every turn **with its citations** as they were when answered. The header
 * shows the collections the chat answers from and the connection · model that
 * writes its answers ("Platform model" for a chat created before the model
 * pick); the owner can change either (audited server-side as
 * `chat.indexes_changed` / `chat.model_changed`).
 *
 * A chat shared with you is read-only (composer disabled rather than offering
 * a Send the backend would refuse). A legacy chat with no collection keeps its
 * history readable and asks for a collection before it can continue.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBooks,
  IconCpu,
  IconInfoCircle,
  IconSend,
} from "@tabler/icons-react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  ChatModelPicker,
  CollectionDocumentPicker,
  chatModelLabel,
  defaultChoice,
  useChatModels,
  type ChatModelChoice,
  type CollectionSelection,
} from "@/features/solutions/shared";
import {
  getChat,
  listMessages,
  sendMessage,
  setChatCollections,
  setChatModel,
  type ChatOut,
  type MessageOut,
} from "@/shared/api/ai";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { ExploreMessage } from "./ExploreMessage";
import { SURFACES, type ChatSurface } from "./surface";

function selectionOf(chat: ChatOut | null): CollectionSelection[] {
  return (chat?.collections ?? []).map((c) => ({
    indexId: c.index_id,
    name: c.name,
    documentIds: null,
  }));
}

export function ExploreChat({ surface = "explore" }: { surface?: ChatSurface }) {
  const config = SURFACES[surface];
  const { chatId = "" } = useParams();
  const getToken = useAccessToken();

  const [chat, setChat] = useState<ChatOut | null>(null);
  const [messages, setMessages] = useState<MessageOut[] | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<CollectionSelection[]>([]);
  const [saving, setSaving] = useState(false);
  const [choosingModel, setChoosingModel] = useState(false);
  const [modelChoice, setModelChoice] = useState<ChatModelChoice>({
    connectionId: null,
    deployment: null,
  });
  const models = useChatModels(choosingModel);
  const modelOptions = models.options;
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const [meta, history] = await Promise.all([
        getChat(token, chatId),
        listMessages(token, chatId),
      ]);
      setChat(meta);
      setMessages(history.items);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load this chat.");
      setMessages([]);
    }
  }, [getToken, chatId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep the newest turn in view as the conversation grows (jsdom has no
  // scrollIntoView, hence the optional call).
  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const readOnly = chat !== null && !chat.is_owner;
  const needsCollection = chat !== null && !chat.grounded;
  const canSend = !readOnly && !needsCollection && !sending && draft.trim().length > 0;

  const onSend = async () => {
    const content = draft.trim();
    if (!canSend) return;
    setSending(true);
    // Show the question at once — retrieval + an answer can take a while.
    const pending: MessageOut = {
      message_id: `pending-${Date.now()}`,
      seq: (messages?.at(-1)?.seq ?? 0) + 1,
      role: "user",
      content,
      model: null,
      model_reported: null,
      ai_inference_id: null,
      total_tokens: null,
      citations: [],
      grounded: null,
      refused: false,
      created_at_utc: new Date().toISOString(),
    };
    setMessages([...(messages ?? []), pending]);
    setDraft("");
    try {
      const token = await getToken();
      const turn = await sendMessage(token, chatId, content);
      setTruncated(turn.history_truncated);
      setMessages((current) => [
        ...(current ?? []).filter((m) => m.message_id !== pending.message_id),
        turn.user_message,
        turn.assistant_message,
      ]);
    } catch (err) {
      toastApiError("Could not answer", err);
      // The question WAS saved server-side, so re-read rather than guess.
      await load();
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void onSend();
    }
  };

  const openEdit = () => {
    setPicked(selectionOf(chat));
    setEditing(true);
  };

  const currentModel: ChatModelChoice = {
    connectionId: chat?.connection_id ?? null,
    deployment: chat?.deployment ?? null,
  };

  const openModel = () => {
    setModelChoice(currentModel);
    setChoosingModel(true);
  };

  // Once the picklist arrives, keep the chat's current pick if it is still
  // offered, else fall back to the first connection / model.
  useEffect(() => {
    if (choosingModel && modelOptions) {
      setModelChoice((current) => defaultChoice(modelOptions, current));
    }
  }, [choosingModel, modelOptions]);

  const onSaveModel = async () => {
    if (!modelChoice.connectionId || !modelChoice.deployment) return;
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await setChatModel(token, chatId, {
        connection_id: modelChoice.connectionId,
        deployment: modelChoice.deployment,
      });
      setChat(updated);
      setChoosingModel(false);
      toastSuccess("Model changed", "New questions will be answered by this model.");
    } catch (err) {
      toastApiError("Could not change the model", err);
    } finally {
      setSaving(false);
    }
  };

  const onSaveCollections = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await setChatCollections(token, chatId, {
        index_ids: picked.map((c) => c.indexId),
      });
      setChat(updated);
      setEditing(false);
      toastSuccess("Collections updated", "New questions will be answered from these.");
    } catch (err) {
      toastApiError("Could not change the collections", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="md" h="100%">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="sm" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
          <Button
            component={RouterLink}
            to={config.basePath}
            variant="subtle"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
          >
            {config.title}
          </Button>
          <Title order={3} lineClamp={1}>
            {chat?.name ?? "Chat"}
          </Title>
          {readOnly && (
            <Badge variant="light" color="gray">
              Read only
            </Badge>
          )}
        </Group>
        {chat && (
          <Group gap={6} data-testid="chat-collections">
            <IconBooks size={16} />
            {(chat.collections ?? []).map((c) => (
              <Badge key={c.index_id} variant="outline" radius="sm" tt="none">
                {c.name}
              </Badge>
            ))}
            {chat.is_owner && (
              <Button variant="subtle" size="xs" onClick={openEdit}>
                {chat.grounded ? "Change" : "Attach a collection"}
              </Button>
            )}
            <IconCpu size={16} style={{ marginLeft: 8 }} />
            <Text size="sm" c="dimmed" data-testid="chat-model">
              {chatModelLabel(chat)}
            </Text>
            {chat.is_owner && (
              <Button variant="subtle" size="xs" onClick={openModel}>
                Change model
              </Button>
            )}
          </Group>
        )}
      </Group>

      {loadError && (
        <Alert variant="light" color="red" icon={<IconAlertTriangle size={18} />}>
          {loadError}
        </Alert>
      )}

      {needsCollection && (
        <Alert
          variant="light"
          color="orange"
          icon={<IconInfoCircle size={18} />}
          title="This chat has no collection"
          data-testid="needs-collection"
        >
          It was created before Explore answered from documents, so its answers have no sources. You
          can read the history; attach a collection to continue the conversation.
        </Alert>
      )}

      {truncated && (
        <Alert variant="light" color="yellow" icon={<IconInfoCircle size={18} />}>
          This conversation is long enough that its oldest turns are no longer used to understand
          follow-up questions. Answers still come only from your collections.
        </Alert>
      )}

      {messages === null && <Loader />}

      {messages !== null && (
        <Stack gap="sm" style={{ flex: 1, overflowY: "auto" }}>
          {messages.length === 0 && !needsCollection && (
            <Text c="dimmed" size="sm">
              Ask a question about the documents in this chat&apos;s collections.
            </Text>
          )}
          {messages.map((message) => (
            <ExploreMessage
              key={message.message_id}
              message={message}
              ticket={config.tickets ? { chatId, canRaise: !readOnly } : null}
            />
          ))}
          {sending && (
            <Group gap="xs" pl="xs" data-testid="searching">
              <Loader size="xs" type="dots" />
              <Text size="sm" c="dimmed">
                Searching your collections…
              </Text>
            </Group>
          )}
          <div ref={bottomRef} />
        </Stack>
      )}

      <Paper withBorder radius="md" p="sm">
        <Stack gap="xs">
          <Textarea
            placeholder={
              readOnly
                ? "This chat was shared with you — you can read it but not add to it."
                : needsCollection
                  ? "Attach a collection to continue this chat."
                  : "Ask about your documents… (Enter to send, Shift+Enter for a new line)"
            }
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={onKeyDown}
            autosize
            minRows={2}
            maxRows={8}
            disabled={readOnly || needsCollection || sending}
            aria-label="Question"
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconSend size={16} />}
              onClick={() => void onSend()}
              loading={sending}
              disabled={!canSend}
            >
              Ask
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Modal opened={choosingModel} onClose={() => setChoosingModel(false)} title="Model" centered>
        <Stack gap="md">
          <ChatModelPicker
            options={modelOptions}
            error={models.error}
            value={modelChoice}
            onChange={setModelChoice}
          />
          <Text size="xs" c="dimmed">
            Earlier answers keep the model that wrote them; new questions use this one.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setChoosingModel(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void onSaveModel()}
              loading={saving}
              disabled={!modelChoice.connectionId || !modelChoice.deployment || saving}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={editing} onClose={() => setEditing(false)} title="Collections" centered>
        <Stack gap="md">
          <CollectionDocumentPicker
            label="Answer from these collections"
            value={picked}
            onChange={setPicked}
            allowDocumentSubset={false}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void onSaveCollections()}
              loading={saving}
              disabled={picked.length === 0 || saving}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
